import { useCallback, useEffect, useRef, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import './AdminAnnouncement.scss';
import images from '../../assets/images';
import TabsContentHeader from '../../components/AdminDashboardTabsContentHeader/TabsContentHeader';
import { useLazyQuery, useMutation } from '@apollo/client';
import { CircularProgress, Menu, Tooltip } from '@mui/material';
import ToasterWithMessage from '../../components/toaster';
import ConfirmationPopup from '../../components/ConfirmationPopup/ConfirmationPopup';
import { debounce } from 'lodash';
import Loader from '../../components/Loader/Loader';
import { getMenuItemsForEditShare, getScreenDimensions } from '../../utils/helper';
import { DELETE_ANNOUNCEMENT, GET_ALL_ANNOUNCEMENTS, SHARE_TO_ALL_ANNOUNCEMENT } from './api/AdminAnnouncementQuery';
import { useClassroomOptionsPrefetch } from './hooks/useClassroomOptionsPrefetch';
import AdminAnnouncementForm from './Components/AdminAnnouncementForm';
import AnnouncementViewer from './Components/AnnouncementViewer';
import MaterialActionsOverlay from '../../components/ActionsOverlay/MaterialActionsOverlay';
import { useAppSelector } from '../../redux/app/hooks';
import { FETCH_POLICY, FETCH_POLICY_NO_CACHE, SCHOOL_ADMIN, STUDENT_ROLE, TEACHER_ROLE } from '../../utils/constants';
import { READ_NOTIFICATIONS } from '../AdminDashboard/api/AdminDashboardQuery';
import { setHideScrollToTopButton, setRefetchNotification } from '../../redux/slices/authSlice';
import { useDispatch } from 'react-redux';
import MobileScreenHeader from '../../components/MobileScreenHeader/MobileScreenHeader';
import { trackButtonClick } from '../../firebase/analyticsHelper';
import { ButtonClickEvents } from '../../firebase/constants/events';

export interface AnnouncementObjectType {
        aId:string;
        aTitle:string;
        aOriginalTitle: string;
        aMessage:string;
        aUrl:string;
        saId:string;
        updatedAt:string;
        isShare: boolean;
        announcementClassroomsByAId?: {
            nodes: {
                cId: string;
            }[];
        };
        feedsByAId: {
            nodes: [
                {
                    fId: string;
                }
            ]
        }
}
interface AnnouncementListType {
    allAnnouncements: {
        nodes: AnnouncementObjectType[];
        totalCount: number;
    }
}

export interface AdminAnnouncementProps {
    isFullView?: boolean;
    setisSearchClick?: (value: boolean) => void;
    isSearchClick?: boolean;
};

const AdminAnnouncement: React.FC<AdminAnnouncementProps> = ({ isFullView, setisSearchClick, isSearchClick }) => {
    const { MoreIcon, AnnouncementIcon, DeleteGif, ShareGif, AnnouncementImage, searchCloseIcon } = images;
    const [announcementsList, setAllAnnouncementsList] = useState<AnnouncementListType>();
    const [hasMore, setHasMore] = useState(true);
    const [offset, setOffset] = useState(0);
    const limit = 50;
    const [displayForm, setDisplayForm] = useState<boolean>(false);
    const [openOverlay, setOpenOverlay] = useState<boolean>(false);
    const [actionData, setActionData] = useState<AnnouncementObjectType>();
    const [displayDetailedView,setDisplayDetailedView] = useState<boolean>(false);
    const [singleAnnouncementDetail, setSingleAnnouncementDetail] = useState<AnnouncementObjectType>();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [refetchData, setRefetchData] = useState<boolean>(false);
    const [openConfirmationPopup, setOpenConfirmationPopup] = useState<boolean>(false);
    const [clickAction, setClickAction] = useState("");
    const [searchValue, setSearchValue] = useState("");
    const { success, error } = ToasterWithMessage();
    const announcementTitleRefs = useRef<Array<HTMLLabelElement | null>>([]);
    const [isOverflow, setIsOverflow] = useState<boolean[]>([]);
    const baseUrl = process.env.REACT_APP_FRONTEND_URL;
    const [action, setAction] = useState<string>('');
    const role = useAppSelector((state) => state.auth.role);
    const profile = useAppSelector((state) => state.auth.adminProfile);
    const dispatch = useDispatch();
    const profileImage = useAppSelector((state) => state?.auth?.adminProfileImage);
    const StudentName = profile?.getCurrentUser?.nodes?.[0]?.uFullName;
    const { isMobileScreen } = getScreenDimensions();
    

    // Warm the cache so the announcement form's classroom options (and "Select All") are ready when it opens
    useClassroomOptionsPrefetch(role === SCHOOL_ADMIN);

    const [getAllAnnouncements] = useLazyQuery(GET_ALL_ANNOUNCEMENTS, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
            setAllAnnouncementsList(prev => {
                if (!prev || offset === 0) {
                    return data;
                }
                // Merge new nodes with previous nodes for infinite scroll
                return {
                    ...data,
                    allAnnouncements: {
                        ...data.allAnnouncements,
                        nodes: [
                            ...(prev.allAnnouncements?.nodes || []),
                            ...(data.allAnnouncements?.nodes || [])
                        ]
                    }
                };
            });
            if (data?.allAnnouncements?.nodes) {
                setHasMore(data.allAnnouncements.nodes.length === limit && (offset + limit) < (data.allAnnouncements.totalCount || 0));
            }
            setRefetchData(false);
        }
    });
    
    const [deleteAnnouncement] = useMutation(DELETE_ANNOUNCEMENT, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
          success("Announcement deleted successfully")
          setClickAction('');
          setRefetchData(true);
        },
        onError: (err) => {
          error(err?.message);
        }
      });

      const [shareToAllAnnouncement] = useMutation(SHARE_TO_ALL_ANNOUNCEMENT, {
        fetchPolicy: FETCH_POLICY_NO_CACHE,
        onCompleted: (data) => {
          if(action === 'undoShare'){
            success("Unshared successfully")
          }else {
            success("Announcement shared successfully")
          }
          setClickAction('');
          setRefetchData(true);
        },
        onError: (err) => {
          error(err?.message);
        }
      });

    const [readNotification] = useMutation(READ_NOTIFICATIONS, {
        fetchPolicy: FETCH_POLICY,
        onCompleted: () => {
            dispatch(setRefetchNotification({ refetchNotification: true }));
        },
        onError: (err) => {
            console.log("Error", err?.message);
        }
    })

    useEffect(() => {
        setOffset(0);
        setHasMore(true);
        getAllAnnouncements({
            variables: {
                searchInput: '%%',
                limit,
                offset: 0
            }
        });
    }, []);

    useEffect(() => {
        if (displayForm === false) {
            setOffset(0);
            setHasMore(true);
            getAllAnnouncements({
                variables: {
                    searchInput: `%${searchValue.trim()}%`,
                    limit,
                    offset: 0
                }
            });
        }
    }, [displayForm]);

    useEffect(() => {
        if (refetchData === true) {
            setOffset(0);
            setHasMore(true);
            getAllAnnouncements({
                variables: {
                    searchInput: `%${searchValue.trim()}%`,
                    limit,
                    offset: 0
                }
            });
        }
    }, [refetchData]);

    //To search classroom
    const handleSearchDebounce = useCallback(
        debounce((value) => {
            setSearchValue(value);
            setOffset(0);
            if(value.trim() === ""){
                setAllAnnouncementsList(undefined);
            }
            if(value?.trim().length >= 3 || value.trim() === "") {
                getAllAnnouncements({
                    variables: {
                        searchInput: `%${value.trim()}%`,
                        limit,
                        offset: 0
                    }
                });
            }
        }, 500),
        [getAllAnnouncements]
    );
    // Infinite scroll fetch more
    const fetchMoreAnnouncements = () => {
        const newOffset = offset + limit;
        setOffset(newOffset);
        getAllAnnouncements({
            variables: {
                searchInput: `%${searchValue.trim()}%`,
                limit,
                offset: newOffset
            }
        });
    };

    //To open actions options
    const handleActions = (event: React.MouseEvent<HTMLElement>, data: AnnouncementObjectType) => {
        event.stopPropagation(); // Stop propagation to prevent triggering parent click
        setAnchorEl(event.currentTarget);
        setActionData(data);
        setSingleAnnouncementDetail(data);
        setOpenOverlay(true);
    };

    //To close actions option
    const handleCloseActions = () => {
        setOpenOverlay(false);
    };


    const handleClickCard = (data: AnnouncementObjectType) => {
        if (openOverlay === false) {
           setSingleAnnouncementDetail(data);
           setDisplayDetailedView(true);
           if (data?.feedsByAId?.nodes[0]?.fId && profile?.getCurrentUser?.nodes[0]?.uId) {
            readNotification({
                variables: {
                    fId: data?.feedsByAId?.nodes[0]?.fId,
                    uId: profile?.getCurrentUser?.nodes[0]?.uId
                }
               })
           }
        }
    };

    //To format classroom total count
    const formatAnnouncementCount = (count: number) => {
        if (count === 0) {
            return "No Announcement";
        } else if (count === 1) {
            return "1 Announcement";
        } else {
            return `${count} Announcements`;
        }
    };
    
    //To check the announcement title is overflow
    const checkOverflow = (index: number) => {
        if (announcementTitleRefs.current[index]) {
            const overflow = announcementTitleRefs.current[index]!.scrollWidth > announcementTitleRefs.current[index]!.clientWidth;
            setIsOverflow(prev => {
                const newOverflow = [...prev];
                newOverflow[index] = overflow;
                return newOverflow;
            });
        }
    };

    //To check overflow in resize 
    useEffect(() => {
        const handleResize = () => {
            announcementsList?.allAnnouncements?.nodes?.forEach((_, index) => {
                checkOverflow(index);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [announcementsList]);

      //To check overflow in resize 
      useEffect(() => {
        if(announcementsList?.allAnnouncements?.nodes){
            announcementsList?.allAnnouncements?.nodes?.forEach((_, index) => {
                checkOverflow(index);
            });
        }

        const handleResize = () => {
            announcementsList?.allAnnouncements?.nodes?.forEach((_, index) => {
                checkOverflow(index);
            });
        };

        window.addEventListener('resize', handleResize);

        return () => {
            window.removeEventListener('resize', handleResize);
        };
    }, [announcementsList]);

    //To done actions in materials
    const handleMaterialActions = (data: AnnouncementObjectType, action: string) => {
        setSingleAnnouncementDetail(data);
        if (action === "edit") {
            trackButtonClick(ButtonClickEvents?.ANNOUNCEMENT_EDIT_BUTTON_CLICK, {
                description: "User clicks the edit button on announcement card"
            });
            setDisplayForm(true);
            setClickAction("edit");
        } else if (action === "delete") {
            // deleteAnnouncement({
            //     variables: {
            //         aId: data?.aId
            //       }
            // })
        }
    };

    const undoShareAnnouncement = () => {
        shareToAllAnnouncement({
            variables: {
                isShare: false,
                aId: actionData?.aId
            }
        })
      }

    const menuItems = handleMaterialActions 
                    ? getMenuItemsForEditShare(handleCloseActions, handleMaterialActions, setOpenConfirmationPopup, setAction, actionData, undoShareAnnouncement)
                    :[];

     // click yes button in confirmation popup
  const onYesButtonClick = () => {
    setOpenConfirmationPopup(false);
    if(action === 'delete'){
        deleteAnnouncement({
            variables: {
              aId: actionData?.aId
            }
          });
    }else if(action === 'shareAll'){
        shareToAllAnnouncement({
            variables: {
                isShare: true,
                aId: actionData?.aId
            }
        })
    }else if(action === 'undoShare'){
        shareToAllAnnouncement({
            variables: {
                isShare: false,
                aId: actionData?.aId
            }
        })
    }
    
  }

  // click no button in confirmation popup
  const onNoButtonClick = () => {
    setOpenConfirmationPopup(false);
  }

    const handleCloseSearch = () => {
        if (searchValue) {
            setSearchValue("");
            handleSearchDebounce("");
        }
        setisSearchClick?.(false);
    };

    const searchOnChange = (event: { target: { value: string } }) => {
        setSearchValue(event.target.value);
        if (handleSearchDebounce) {
            handleSearchDebounce(event?.target?.value);
        }
    };

    useEffect(() => {
        dispatch(setHideScrollToTopButton({hideScrollToTopButton: !displayDetailedView}));
    }, [displayDetailedView])

    useEffect(() => {
        dispatch(setHideScrollToTopButton({hideScrollToTopButton: !displayForm}));
    }, [displayForm])

    return (
        <>
            {displayDetailedView ? 
                    <>
                      <AnnouncementViewer
                       displayDetailedView={displayDetailedView}
                       setDisplayDetailedView={setDisplayDetailedView}
                       singleAnnouncementDetail={singleAnnouncementDetail}
                      />
                    </>
                   : displayForm ?
                    <>
                        <AdminAnnouncementForm
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            setClickAction={setClickAction}
                            clickAction={clickAction}
                            singleAnnouncementDetail={singleAnnouncementDetail}
                            // isFullView={isFullView}
                        />
                    </>
                    :
                    <div className={role === SCHOOL_ADMIN ?'AdminAnnouncement':'TeaacherStudentAnnouncement'}>
                        {(getScreenDimensions()?.isMobileScreen && role !== SCHOOL_ADMIN) && (
                            <MobileScreenHeader
                                profileImage={profileImage}
                                role={role}
                                studentName={StudentName}
                                profile={profile}
                            />
                        )}
                       {
                        (role === STUDENT_ROLE || role === TEACHER_ROLE) ?
                        <div className='announcementHeader'>
                            <div className='announcementHeaderTitle'>Announcement</div>
                            <span className='announcementHeaderDescription'>News from administration.</span>
                        </div>
                        :
                        <>
                        {(role === SCHOOL_ADMIN && isMobileScreen && isSearchClick) ?
                            <div className={'admin-announcement-search'}>
                                    <input id="searchBox" type='text' placeholder='Search' onChange={searchOnChange} value={searchValue} className='SearchInputFieldStyle' />
                                    {(getScreenDimensions()?.isMobileScreen && isSearchClick) && <img src={searchCloseIcon} alt='closeIcon' className='closeIcon' onClick={handleCloseSearch} />}
                            </div>
                        :
                        <TabsContentHeader
                            title={"All Messages"}
                            subtitle={formatAnnouncementCount(announcementsList?.allAnnouncements?.totalCount || 0)}
                            addLabel={"Add Annoucement"}
                            setClickAction={setClickAction}
                            setDisplayForm={setDisplayForm}
                            displayForm={displayForm}
                            subImage={AnnouncementIcon}
                            handleSearchDebounce={handleSearchDebounce}
                            searchValue={searchValue}
                            setSearchValue={setSearchValue}
                            isFullView={isFullView}
                        />}
                        {isMobileScreen && !isSearchClick && <div className='divider' />}
                        </>
                       }
                        {
                            (announcementsList === undefined) ? <Loader bottom='150px' />
                                :
                                (
                                    <InfiniteScroll
                                        dataLength={announcementsList?.allAnnouncements?.nodes?.length || 0}
                                        next={fetchMoreAnnouncements}
                                        hasMore={hasMore}
                                        loader={
                                            <div className="scroll-loader">
                                                <CircularProgress size={30} />
                                            </div>
                                        }
                                        height={`100%`}
                                        className={`${announcementsList?.allAnnouncements?.totalCount ? 'anouncementListContainer' : 'forNoDataFound'}`}
                                    >
                                        {
                                            announcementsList?.allAnnouncements?.totalCount ?
                                                announcementsList?.allAnnouncements?.nodes?.map((cardItem: AnnouncementObjectType, index: number) => {
                                                    return (
                                                        <div className='announcementCardContainer' key={`${index}-${cardItem?.aId}`} onClick={() => handleClickCard(cardItem)}>
                                                            <img className='announcementImg' src={cardItem?.aUrl ? baseUrl+cardItem?.aUrl : AnnouncementImage} alt='announcementImage'/>
                                                            {getScreenDimensions()?.isMobileScreen ? <>
                                                                <div className='right-container'>
                                                                    <div className='topSectionTitle'>
                                                                        <Tooltip 
                                                                        title={cardItem?.aOriginalTitle}
                                                                        disableHoverListener={!isOverflow[index]}
                                                                        placement="top"
                                                                        >
                                                                            <label className='announcementTitleStyle' ref={(el) => (announcementTitleRefs.current[index] = el)} onMouseEnter={() => checkOverflow(index)}>
                                                                                {cardItem?.aOriginalTitle}
                                                                            </label>
                                                                        </Tooltip>
                                                                        {role === SCHOOL_ADMIN && <div className='action-btn' onClick={(event) => handleActions(event, cardItem)}>
                                                                            <img src={MoreIcon} alt="moreIcon" className='moreIconStyle' />
                                                                        </div>}
                                                                    </div>
                                                                    <div className='messageCtn'>
                                                                        {cardItem?.aMessage}
                                                                    </div>
                                                                </div>
                                                            </> : <>
                                                                <div className='topSectionTitle'>
                                                                    <Tooltip 
                                                                    title={cardItem?.aOriginalTitle}
                                                                    disableHoverListener={!isOverflow[index]}
                                                                    placement="top"
                                                                    >
                                                                        <label className='announcementTitleStyle' ref={(el) => (announcementTitleRefs.current[index] = el)} onMouseEnter={() => checkOverflow(index)}>
                                                                            {cardItem?.aOriginalTitle}
                                                                        </label>
                                                                    </Tooltip>
                                                                    {role === SCHOOL_ADMIN && <div className='action-btn' onClick={(event) => handleActions(event, cardItem)}>
                                                                        <img src={MoreIcon} alt="moreIcon" className='moreIconStyle' />
                                                                    </div>}
                                                                </div>
                                                                <div className='messageCtn'>
                                                                    {cardItem?.aMessage}
                                                                </div>
                                                            </>}
                                                        </div>
                                                    )
                                                })
                                                :
                                                <div className='noDataFoundStyle'>No data found</div>
                                        }
                                    </InfiniteScroll>
                                )
                        }

                        <Menu
                            id="action-menu"
                            MenuListProps={{
                                "aria-labelledby": "long-button",
                                disablePadding: true,
                            }}
                            anchorEl={anchorEl}
                            open={openOverlay}
                            onClose={handleCloseActions}
                            anchorOrigin={{
                                vertical: 'bottom',
                                horizontal: 'right',
                            }}
                            transformOrigin={{
                                vertical: 'top',
                                horizontal: 'right',
                            }}
                            sx={{
                                '& .MuiPaper-root': {
                                    borderRadius: '5px',
                                    boxShadow: '-10px 10px 15px 0.2px rgba(0, 0, 0, 0.05)',
                                },
                            }}
                        >
                            {actionData && <MaterialActionsOverlay
                                anchorEl={anchorEl}
                                open={openOverlay}
                                handleClose={handleCloseActions}
                                menuItems={menuItems}
                                actionsHeight={'103px'}
                                position="right"
                                verticalPosition = "bottom"
                               />}
                        </Menu>
                        <ConfirmationPopup
                            open={openConfirmationPopup}
                            message={action === 'delete' ?`Are you sure you want to Delete This Announcement?` : `Are you sure you want to share this announcement to all the selected classrooms?`}
                            gifData={action === 'delete' ? DeleteGif : ShareGif}
                            yesButtonFunctionality={onYesButtonClick}
                            noButtonFunctionality={onNoButtonClick}
                            messageWidth={'195px'}
                        />
                    </div> 
            }
        </>
    )
}
export default AdminAnnouncement;
